'use client';

import { useTranslations } from 'next-intl';
import { FC, useEffect, useMemo, useState } from 'react';
import { useTopHolders } from '@/lib/api/transaction/hooks';
import type { TopHolder as TopHolderData } from '@/lib/api/transaction/models';
import { GlowName } from '@/lib/components/RewardsHub/GlowName';
import { Link } from '@/lib/routing';
import { useOrderFormStore } from '@/lib/store/order-form-store';
import { MarketItem } from '@/lib/types/markets/market';
import { InlineEmpty } from '../ui-kit/inline-empty';
import { Spinner } from '../ui-kit/spinner';
import { Text } from '../ui-kit/text';
import { cn } from '../ui-kit/utils';
import { UserAvatar } from '../ui-kit-v2/user-avatar';
import { Empty } from './Empty';
import { TopHoldersFilter } from './TopHoldersFilter';

type TopHoldersProps = {
  outcomes: MarketItem[];
};

export const TopHolders: FC<TopHoldersProps> = ({ outcomes }) => {
  const t = useTranslations('topHolders');
  const storeOutcomeId = useOrderFormStore.use.selectedOutcomeId();
  // Select first outcome by default
  const [selectedOutcomeId, setSelectedOutcomeId] = useState(
    storeOutcomeId || outcomes[0]?.id || '',
  );

  // Sync with order form: when outcome changes in order form, update top holders
  useEffect(() => {
    if (storeOutcomeId && outcomes.some((o) => o.id === storeOutcomeId)) {
      setSelectedOutcomeId(storeOutcomeId);
    }
  }, [storeOutcomeId, outcomes]);

  // Find selected outcome
  const selectedOutcome = useMemo(
    () => outcomes.find((o) => o.id === selectedOutcomeId) || outcomes[0],
    [outcomes, selectedOutcomeId],
  );

  // Get YES and NO token IDs from resolutions
  const yesTokenId = useMemo(() => {
    return selectedOutcome?.r?.find((r) => r.isPositive === true)?.id || '';
  }, [selectedOutcome]);

  const noTokenId = useMemo(() => {
    return selectedOutcome?.r?.find((r) => r.isPositive === false)?.id || '';
  }, [selectedOutcome]);

  // Fetch top holders for YES token
  const {
    data: yesHoldersData,
    isLoading: isLoadingYes,
    error: errorYes,
  } = useTopHolders({
    eventTokenId: yesTokenId,
    period: 'ALL',
  });

  // Fetch top holders for NO token
  const {
    data: noHoldersData,
    isLoading: isLoadingNo,
    error: errorNo,
  } = useTopHolders({
    eventTokenId: noTokenId,
    period: 'ALL',
  });

  const outcomeOptions = useMemo(
    () =>
      outcomes.map((outcome) => ({
        label: outcome.groupItemTitle,
        value: outcome.id,
      })),
    [outcomes],
  );

  const isLoading = isLoadingYes || isLoadingNo;
  const hasError = errorYes || errorNo;

  const yesHoldersExist = yesHoldersData && yesHoldersData.items?.length > 0;
  const noHoldersExist = noHoldersData && noHoldersData.items?.length > 0;

  return (
    <div className="flex flex-col gap-4">
      {/* Outcome selector - only show if multiple outcomes */}
      {outcomes.length > 1 && (
        <TopHoldersFilter
          onChange={setSelectedOutcomeId}
          value={selectedOutcomeId}
          options={outcomeOptions}
          contentClassName="min-w-[150px] max-w-[250px]"
        />
      )}

      {!yesHoldersExist && !noHoldersExist && !isLoading && (
        <div className="flex items-center justify-center py-8">
          <Empty title={t('empty.title')} desc={t('empty.description')} />
        </div>
      )}

      {/* Loading state */}
      {isLoading && (
        <div className="flex items-center justify-center py-8">
          <Spinner size={36} className="!text-v2-text-secondary" />
        </div>
      )}

      {/* Error state */}
      {hasError && !isLoading && (
        <div className="flex items-center justify-center py-8">
          <Text variant="body-small" className="text-destructive">
            {t('error')}
          </Text>
        </div>
      )}

      {/* Content - two columns for YES and NO */}
      {!isLoading && !hasError && yesHoldersExist && noHoldersExist && (
        <div className="flex items-start gap-6">
          <TopHolderColumn
            title={t('yesHolders')}
            holders={yesHoldersData?.items || []}
            type="yes"
            noHoldersYet={t('noHoldersYet')}
            shares={t('shares')}
          />
          <TopHolderColumn
            title={t('noHolders')}
            holders={noHoldersData?.items || []}
            type="no"
            noHoldersYet={t('noHoldersYet')}
            shares={t('shares')}
          />
        </div>
      )}
    </div>
  );
};

type TopHolderColumnProps = {
  title: string;
  holders: TopHolderData[];
  type: 'yes' | 'no';
  noHoldersYet: string;
  shares: string;
};

const TopHolderColumn: FC<TopHolderColumnProps> = ({
  title,
  holders,
  type,
  noHoldersYet,
  shares,
}) => {
  return (
    <div className="flex min-w-0 flex-1 flex-col items-start gap-4">
      <div className="flex w-full items-center py-2">
        <p
          className={cn(
            'text-[13px] font-normal leading-4 tracking-[-0.13px] whitespace-nowrap',
            type === 'yes' ? 'text-v2-green' : 'text-[#fb4e4b]',
          )}
        >
          {title}
        </p>
      </div>

      {holders.length === 0 ? (
        <div className="flex w-full items-center justify-center py-4">
          <InlineEmpty>{noHoldersYet}</InlineEmpty>
        </div>
      ) : (
        holders.map((holder, idx) => (
          <Link
            key={idx}
            href={`/stats/profile/${holder.userId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex w-full items-center gap-3 hover:opacity-80"
          >
            <UserAvatar
              userId={holder.userId}
              name={holder.name}
              look={holder.look}
              size={36}
              className="shrink-0 border border-solid border-v2-border"
            />
            <div className="flex min-w-0 flex-1 flex-col justify-center gap-1">
              <p className="truncate text-[13px] font-medium leading-4 tracking-[-0.13px] text-v2-text-primary hover:underline">
                <GlowName glow={holder.look?.glow ?? null}>{holder.name}</GlowName>
              </p>
              <p className="text-[12px] font-normal leading-4 tracking-[-0.12px] text-v2-text-secondary">
                {holder.quantity} {shares}
              </p>
            </div>
          </Link>
        ))
      )}
    </div>
  );
};
